import React, { ChangeEvent, FC, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Theme, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Keyboard, { KeyboardReactInterface } from 'react-simple-keyboard';
import { useTranslation } from 'react-i18next';

import { RedditTextField } from 'shared/components';
import { PrevButton, NextButton } from 'kioskApp/components';
import { Form, FormField } from 'kioskApp/models/form';
import { ErrorCodes } from 'shared/helpers';
import validators, { ValidatorOptions } from 'shared/validators/Index';
import { CheckAccountNumberModal } from 'kioskApp/containers';
import { ThemeType } from 'shared/interfaces';
import { useStore } from 'kioskApp/hooks';

const useStyles = makeStyles((globalTheme: Theme) => {
    return {
        root: {
            padding: '0 60px',
            height: '100%',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
        },
        title: {
            display: 'flex',
            flexDirection: 'row',
            marginTop: '5vh',
            alignItems: 'center',
            textAlign: 'center',
            '& svg': { marginRight: '25px' },
        },
        body: {
            marginTop: '100px',
            display: 'flex',
            flexDirection: 'column',
        },
        inputTitle: {
            marginBottom: '48px',
        },
        inputConteiner: {
            width: '502px',
            alignSelf: 'center',
        },
        input: {
            marginBottom: '20px',
        },
        inputError: {
            marginLeft: 0,
            fontSize: '1.75rem !important',
        },
        keyboard: {
            width: '100%',
            alignSelf: 'center',
            backgroundColor: globalTheme.palette.background.default,
            paddingRight: '2px',
            paddingBottom: '2px',
            '& .hg-row:not(:last-child)': {
                marginBottom: '32px !important',
            },
            '& .hg-row': {
                display: 'flex',
            },
            display: 'flex !important',
        },
        keyboardButton: {
            border: (theme: ThemeType) =>
                `1px solid ${theme.secondaryButton.borderColor}`,
            boxShadow: (theme: ThemeType) =>
                `2px 2px 0px ${theme.secondaryButton.borderColor}`,
            borderRadius: (theme: ThemeType) => `${theme.buttonRadius}px`,
            width: '144px',
            height: '96px',
            fontSize: '3rem',
            fontWeight: 'bold',
            color: (theme: ThemeType) => theme.secondaryButton.fontColor,
            background: (theme: ThemeType) =>
                theme.secondaryButton.backgroundColor,
            display: 'flex !important',
            alignItems: 'center',
            justifyContent: 'center',
            '&:not(:last-child)': {
                marginRight: '32px !important',
            },
            '&.hg-activeButton': {
                backgroundColor: '#0084d61c',
            },
            fontFamily: 'Altice',
        },
        keyboardSpecialButton: {
            fontSize: '1.5rem',
        },
        footer: {
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            marginTop: '80px',
        },
    };
});

export const MobileAccountLookupStep: FC = observer(() => {
    const accountNumberFieldName = 'accountNumber';
    const areaCodeFieldName = 'areaCode';
    const clearKeyCode: string = '{clear}';

    const { t } = useTranslation();
    const Store = useStore();
    const { billpay, wizard } = Store;
    const classes = useStyles(Store.configuration.theme);

    const keyboard = useRef<KeyboardReactInterface | null>(null);

    const [activeInput, setActiveInput] = useState(accountNumberFieldName);

    const [form] = useState(
        new Form().initFromObject({
            [accountNumberFieldName]: [
                '',
                [
                    validators.required(),
                    validators.regex(new RegExp(/(^[0-9]+$|^$)/)),
                    validators.minlength(13, new ValidatorOptions(true)),
                ],
            ],
            [areaCodeFieldName]: [
                '',
                [
                    validators.required(),
                    validators.regex(new RegExp(/(^[0-9]+$|^$)/)),
                    validators.minlength(5, new ValidatorOptions(true)),
                ],
            ],
        })
    );

    const [warningModalOpen, setWarningModalOpen] = useState(false);

    const keyboardLayout = {
        default: ['1 2 3', '4 5 6', '7 8 9', `{bksp} 0 ${clearKeyCode}`],
    };

    const keyboardDisplay = {
        '{bksp}': t('DEL'),
        [clearKeyCode]: t('CLEAR'),
    };

    const onChangeInput = (
        field: FormField,
        event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        let inputVal = event.target.value;

        field.setValue(inputVal);
        keyboard.current?.setInput(inputVal);
    };

    const onChangeAll = (input: string) => {
        const field = form.fieldByName(activeInput);

        field && field.setValue(input);

        // Focus next field, if first is filled
        if (activeInput === accountNumberFieldName) {
            const accountNumberField = form.fieldByName(accountNumberFieldName);
            const areaCodeField = form.fieldByName(areaCodeFieldName);

            if (accountNumberField && accountNumberField.value.length === 13) {
                areaCodeField!.focus();
            }
        }
    };

    const prev = () => {
        Store.kioskSdk.goBack();
    };

    const next = () => {
        const accountNumberField = form.fieldByName(accountNumberFieldName);

        if (!form.validateAll()) {
            return;
        }

        billpay
            .getCustomerData({
                accountNumber: form.value[accountNumberFieldName],
                zip: form.value[areaCodeFieldName],
                mobileNumber: undefined,
            })
            .then(
                (_) => {
                    wizard.goNext();
                },
                (response) => {
                    if (response.data) {
                        const errors = response.data.errors || [];
                        const accountNotFoundError = errors.find(
                            (e: any) => e.code === ErrorCodes.accountNotFound
                        );

                        if (accountNotFoundError) {
                            accountNumberField!.setError(
                                t('Account was not found')
                            );
                            return;
                        }
                    }

                    setWarningModalOpen(true);
                }
            );
    };

    const fields = form.fieldsObj;

    return (
        <div className={classes.root}>
            <div className={classes.title}>
                <Typography variant='h2'>{t('Search Account')}</Typography>
            </div>

            <div className={classes.body}>
                <div className={classes.inputTitle}>
                    <Typography variant='h4'>
                        {t(
                            'Please enter your Account Number and Zip Code, then press Next'
                        )}
                    </Typography>
                </div>

                <div className={classes.inputConteiner}>
                    <div className={classes.input}>
                        <RedditTextField
                            inputRef={fields[accountNumberFieldName].ref}
                            onFocus={(a: any) => {
                                activeInput !==
                                    fields[accountNumberFieldName].name &&
                                    setActiveInput(
                                        fields[accountNumberFieldName].name
                                    );
                            }}
                            onChange={(e: any) => {
                                onChangeInput(
                                    fields[accountNumberFieldName],
                                    e
                                );

                                // Focus next field, if first is filled
                                if (
                                    fields[accountNumberFieldName].value
                                        .length === 13
                                ) {
                                    fields[areaCodeFieldName].focus();
                                }
                            }}
                            inputProps={{ maxLength: '13' }}
                            FormHelperTextProps={{
                                classes: {
                                    root: classes.inputError,
                                },
                            }}
                            fullWidth
                            variant='filled'
                            label={t('Account Number')}
                            placeholder={'1234567890123'}
                            error={fields[accountNumberFieldName].hasError}
                            helperText={
                                fields[accountNumberFieldName].hasError &&
                                fields[accountNumberFieldName].error
                            }
                            value={fields[accountNumberFieldName].value}
                        />
                    </div>
                    <div className={classes.input}>
                        <RedditTextField
                            inputRef={fields.areaCode.ref}
                            onFocus={(e: any) => {
                                activeInput !== fields.areaCode.name &&
                                    setActiveInput(fields.areaCode.name);
                            }}
                            onChange={(e: any) =>
                                onChangeInput(fields.areaCode, e)
                            }
                            inputProps={{ maxLength: '5' }}
                            FormHelperTextProps={{
                                classes: {
                                    root: classes.inputError,
                                },
                            }}
                            fullWidth
                            variant='filled'
                            label={t('ZIP Code')}
                            placeholder={'12345'}
                            error={fields.areaCode.hasError}
                            helperText={
                                fields.areaCode.hasError &&
                                fields.areaCode.error
                            }
                            value={fields.areaCode.value}
                        />
                    </div>

                    <Keyboard
                        keyboardRef={(r) => (keyboard.current = r)}
                        inputName={activeInput}
                        layout={keyboardLayout}
                        layoutName={'default'}
                        display={keyboardDisplay}
                        theme={classes.keyboard}
                        preventMouseDownDefault={true}
                        disableButtonHold={true}
                        maxLength={{
                            [areaCodeFieldName]: 5,
                            [accountNumberFieldName]: 13,
                        }}
                        buttonTheme={[
                            {
                                class: classes.keyboardButton,
                                buttons: `{bksp} ${clearKeyCode} 0 1 2 3 4 5 6 7 8 9`,
                            },
                            {
                                class: classes.keyboardSpecialButton,
                                buttons: `{bksp} ${clearKeyCode}`,
                            },
                        ]}
                        onKeyPress={(e: any) => {
                            //clear currenly active input
                            if (e === clearKeyCode) {
                                onChangeAll('');
                                keyboard.current?.clearInput(activeInput);
                            }
                        }}
                        onChange={onChangeAll}
                    />
                </div>
            </div>

            <CheckAccountNumberModal
                modalProps={{
                    open: warningModalOpen,
                    onClose: () => {
                        setWarningModalOpen(false);
                    },
                }}
                buttonProps={{
                    onClick: () => {
                        setWarningModalOpen(false);
                    },
                }}
            />

            <div className={classes.footer}>
                <PrevButton
                    buttonProps={{
                        onClick: prev,
                    }}
                />
                <NextButton
                    buttonProps={{
                        disabled: form.hasErrors || !form.hasChanges,
                        onClick: next,
                    }}
                />
            </div>
        </div>
    );
});
